// src/components/Expenses/ExpenseForm.jsx
import React, { useState } from 'react';
import { expenseService } from '../../services/expenses';
import { toast } from 'react-hot-toast';

export default function ExpenseForm({ expense, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    category: expense?.category || 'other',
    description: expense?.description || '',
    amount: expense?.amount || '',
    tax_amount: expense?.tax_amount || '',
    expense_date: expense?.expense_date || new Date().toISOString().split('T')[0],
    vendor_name: expense?.vendor_name || '',
  });
  const [loading, setLoading] = useState(false);

  const categories = ['rent', 'salaries', 'stock_purchase', 'utilities', 'transport', 'maintenance', 'marketing', 'other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ Validate required fields
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.description || formData.description.trim() === '') {
      toast.error('Please enter a description');
      return;
    }
    
    if (!formData.amount || formData.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.expense_date) {
      toast.error('Please select an expense date');
      return;
    }
    
    setLoading(true);
    try {
      // ✅ Create the data object explicitly
      const submitData = {
        category: String(formData.category),
        description: String(formData.description).trim(),
        amount: Number(formData.amount),
        tax_amount: formData.tax_amount ? Number(formData.tax_amount) : 0,
        expense_date: String(formData.expense_date),
        vendor_name: formData.vendor_name ? String(formData.vendor_name).trim() : null
      };
      
      console.log('📤 Submitting expense data:', submitData);
      
      let response;
      if (expense) {
        response = await expenseService.updateExpense(expense.id, submitData);
      } else {
        response = await expenseService.createExpense(submitData);
      }
      
      console.log('📥 Response:', response);
      toast.success(expense ? 'Expense updated successfully' : 'Expense recorded successfully');
      onSuccess();
    } catch (error) {
      console.error('Expense form error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat.replace('_', ' ').toUpperCase()}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES) *</label>
          <input
            type="number"
            step="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount (KES)</label>
          <input
            type="number"
            step="0.01"
            value={formData.tax_amount}
            onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date *</label>
          <input
            type="date"
            value={formData.expense_date}
            onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
          <input
            type="text"
            value={formData.vendor_name}
            onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Optional"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
            required
            placeholder="Describe the expense..."
          />
        </div>
      </div>
      <div className="flex space-x-3 pt-4">
        <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Saving...' : (expense ? 'Update' : 'Create')}
        </button>
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
          Cancel
        </button>
      </div>
    </form>
  );
}